import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "../ui/button";

const DISMISS_KEY = "pwaInstallDismissedAt";
const INSTALLED_KEY = "pwaInstalledAt";
const DISMISS_TTL_DAYS = 14;

function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return isIos && isSafari;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true
  );
}

function dismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_TTL_DAYS;
  } catch {
    return false;
  }
}

/**
 * PWAInstallPrompt
 * - On Android/Chrome/Edge: captures beforeinstallprompt and shows a banner with an Install CTA.
 * - On iOS Safari: shows an instructional banner ("Tap Share → Add to Home Screen").
 * - Dismissal persists for 14 days (localStorage).
 * - Auto-hides when running standalone or after installation.
 */
export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;
    if (localStorage.getItem(INSTALLED_KEY)) return undefined;
    if (dismissedRecently()) return undefined;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installedHandler = () => {
      try {
        localStorage.setItem(INSTALLED_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", installedHandler);

    // iOS Safari fallback: no beforeinstallprompt available
    if (isIosSafari()) {
      // Slight delay so we don't compete with onboarding wizard
      const t = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handler);
        window.removeEventListener("appinstalled", installedHandler);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        try {
          localStorage.setItem(INSTALLED_KEY, String(Date.now()));
        } catch {
          /* ignore */
        }
      } else {
        dismiss();
      }
    } catch {
      /* ignore */
    } finally {
      setDeferredPrompt(null);
      setVisible(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-3 right-3 md:left-auto md:right-6 md:bottom-6 bottom-24 z-[60] md:max-w-sm"
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label="Installa app"
    >
      <div className="rounded-2xl border border-slate-200 bg-white shadow-lg p-4 flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center shrink-0">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-slate-900">
            Installa Calcio Club
          </div>
          {iosHint ? (
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Tocca{" "}
              <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-medium">
                Condividi
              </span>{" "}
              nel browser, poi scegli{" "}
              <span className="font-medium">{`"Aggiungi a Home"`}</span> per
              aprire l{"'"}app come nativa.
            </p>
          ) : (
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Aggiungi l{"'"}app alla schermata Home: notifiche più rapide, accesso
              offline e nessuna barra del browser.
            </p>
          )}

          {!iosHint && (
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={install}
                className="rounded-full bg-sky-500 hover:bg-sky-600"
                data-testid="pwa-install-accept-btn"
              >
                Installa
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={dismiss}
                data-testid="pwa-install-dismiss-btn"
              >
                Più tardi
              </Button>
            </div>
          )}
        </div>
        <button
          onClick={dismiss}
          className="text-slate-400 hover:text-slate-700 shrink-0"
          aria-label="Chiudi"
          data-testid="pwa-install-close-btn"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
